import { Head, useForm } from "@inertiajs/react"
import { Camera } from "lucide-react"
import { useState } from "react"

import FileUpload from "@/components/file-upload"
import LocationSelect from "@/components/location-select"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import type RequestsUpdateScoutProfile from "@/types/serializers/RequestsUpdateScoutProfile"
import type ScoutProfile from "@/types/serializers/ScoutProfile"

export default function ScoutEdit({ scout }: { scout: ScoutProfile }) {
  const [avatarUploaded, setAvatarUploaded] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    scout.avatar_url,
  )
  const { data, setData, patch, processing, errors } =
    useForm<RequestsUpdateScoutProfile>({
      scout_profile: {
        organization_name: scout.organization_name,
        role_title: scout.role_title,
        bio: scout.bio ?? "",
        country: scout.country ?? "",
        region: scout.region ?? "",
        city: scout.city ?? "",
        avatar: null,
      },
    })

  const setField = (
    field: keyof typeof data.scout_profile,
    value: string | null,
  ) =>
    setData("scout_profile", {
      ...data.scout_profile,
      [field]: value,
    } as typeof data.scout_profile)

  function handleCountryChange(newCountry: string) {
    setData("scout_profile", {
      ...data.scout_profile,
      country: newCountry,
      region: "",
      city: "",
    })
  }

  function handleRegionChange(newRegion: string) {
    setData("scout_profile", {
      ...data.scout_profile,
      region: newRegion,
      city: "",
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch("/perfil")
  }

  return (
    <AppLayout>
      <Head title="Editar perfil | KrownFT" />

      <div className="mx-auto max-w-2xl px-6 py-8 pb-24">
        <h1 className="mb-6 text-2xl font-bold">Editar perfil</h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          <section>
            <h2 className="mb-4 text-lg font-semibold">Foto de perfil</h2>
            <div className="flex items-center gap-6">
              <div className="group relative">
                <Avatar className="h-24 w-24">
                  {avatarPreview ? (
                    <AvatarImage
                      src={avatarPreview}
                      alt="Foto de perfil actual"
                      className="object-cover"
                    />
                  ) : null}
                  <AvatarFallback className="bg-krown-600 text-2xl font-bold text-white">
                    {scout.user.first_name[0]}
                    {scout.user.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                  <Camera className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <FileUpload
                  accept="image"
                  onUploadComplete={(signedId, file) => {
                    setField("avatar", signedId)
                    setAvatarUploaded(true)
                    const reader = new FileReader()
                    reader.onload = (e) =>
                      setAvatarPreview(e.target?.result as string)
                    reader.readAsDataURL(file)
                  }}
                />
                {avatarUploaded && (
                  <p className="mt-2 text-xs text-green-400">
                    Foto subida correctamente
                  </p>
                )}
                {errors["scout_profile.avatar"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["scout_profile.avatar"]}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Información Profesional */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">
              Información Profesional
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block">Organización</Label>
                <Input
                  value={String(data.scout_profile.organization_name)}
                  onChange={(e) =>
                    setField("organization_name", e.target.value)
                  }
                  placeholder="Ej: Club Atlético Boca Juniors"
                />
                {errors["scout_profile.organization_name"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["scout_profile.organization_name"]?.[0]}
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1.5 block">Cargo</Label>
                <Input
                  value={String(data.scout_profile.role_title)}
                  onChange={(e) => setField("role_title", e.target.value)}
                  placeholder="Ej: Scout Regional, Director de Scouting"
                />
                {errors["scout_profile.role_title"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["scout_profile.role_title"]?.[0]}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Ubicación */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Ubicación</h2>
            <div className="mt-4 grid gap-4">
              <LocationSelect
                country={data.scout_profile.country}
                region={String(data.scout_profile.region)}
                city={String(data.scout_profile.city)}
                onCountryChange={handleCountryChange}
                onRegionChange={handleRegionChange}
                onCityChange={(v) => setField("city", v)}
                errors={{
                  country: errors["scout_profile.country"]?.[0],
                  region: errors["scout_profile.region"]?.[0],
                  city: errors["scout_profile.city"]?.[0],
                }}
              />
            </div>
          </section>

          {/* Sobre Mí */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Sobre Mí</h2>
            <div className="mt-4 grid gap-4">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block">Bio</Label>
                <Textarea
                  value={String(data.scout_profile.bio)}
                  onChange={(e) => setField("bio", e.target.value)}
                  placeholder="Contá sobre tu experiencia como scout..."
                  maxLength={500}
                  rows={4}
                />
                {errors["scout_profile.bio"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["scout_profile.bio"]?.[0]}
                  </p>
                )}
                <p className="mt-1 text-xs text-neutral-500">
                  {String(data.scout_profile.bio).length}/500
                </p>
              </div>
            </div>
          </section>

          <Button
            type="submit"
            disabled={processing}
            className="bg-krown-400 hover:bg-krown-300 w-full"
          >
            Guardar cambios
          </Button>
        </form>
      </div>
    </AppLayout>
  )
}
